/**
 * Duels (1v1) screen — rates + funnel + outcomes + timeseries.
 *
 * Data source: GET /admin/metrics/duels (Phase C).
 * CSV: format=csv branch on the same endpoint.
 */

import { api, ApiError } from '../../api.js';
import { navigate } from '../../router.js';
import { toast } from '../../components/toast.js';
import {
  downloadCsv,
  errorState,
  escape,
  fetchOrNull,
  fmtBps,
  fmtInt,
  fmtKobo,
  fmtRelTime,
  funnelStages,
  kpi,
  mountInteractiveBarChart,
  mountLineChart,
  mountSortableTable,
  rangeQuery,
  skeletonGrid,
} from './common.js';
import { integrityChips, integritySortKey } from './integrity.js';

export async function renderDuels(host, ctx) {
  host.innerHTML = skeletonGrid(6);
  try {
    // Top-players + recent-list are optional — a slow / broken call on
    // either shouldn't take the whole page down.
    const [data, players, recent] = await Promise.all([
      api(`/admin/metrics/duels${rangeQuery(ctx.range)}`),
      fetchOrNull('/admin/metrics/duel-players?limit=25&sort=games&order=desc'),
      fetchOrNull(_recentDuelsPath(ctx.range)),
    ]);
    host.innerHTML = _render(data);
    _mountCharts(host, data);
    _mountDailyCharts(host, data);
    _mountTopPlayers(host, ctx, players);
    _mountRecentDuels(host, ctx, recent);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = errorState(err);
  }
}

/**
 * Build the /admin/duels/list query. The list endpoint accepts ISO
 * start + end; if the range picker's dates are calendar-day (YYYY-MM-DD)
 * we let the backend default to `end.toISOString()` on the far side.
 */
function _recentDuelsPath(range) {
  if (!range || !range.start || !range.end) {
    return '/admin/duels/list?limit=100';
  }
  const start = `${range.start}T00:00:00.000Z`;
  const end = `${range.end}T23:59:59.999Z`;
  return `/admin/duels/list?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&limit=100`;
}

export function exportCsv({ range }) {
  const qs = rangeQuery(range);
  const path = qs ? `/admin/metrics/duels${qs}&format=csv` : `/admin/metrics/duels?format=csv`;
  downloadCsv(path, `triviana_duels_${range.start}_${range.end}.csv`);
}

function _render(d) {
  const t = d.totals || {};
  const series = d.timeseries || [];
  const created = Number(t.duels_created || 0);
  const accepted = Number(t.duels_accepted || 0);
  const completed = Number(t.duels_completed || 0);
  const abandoned = Number(t.duels_abandoned || 0);
  const declined = Number(t.duels_declined || 0);

  // Snapshot cards — not windowed. Answer "how many duels are there
  // right now?" at three lifecycle points, plus windowed stake +
  // rake volume so the money numbers sit alongside the counts.
  const snapshot = `
    <div class="adm2-section-label">Duels · at a glance</div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));">
      ${kpi({ label: 'Played · all time', value: fmtInt(t.duels_played_all_time), hint: 'completed duels', hero: true })}
      ${kpi({ label: 'Playing now', value: fmtInt(t.duels_playing_now), hint: 'matched · awaiting opponent' })}
      ${kpi({ label: 'Published', value: fmtInt(t.duels_published_now), hint: 'open · public browse queue' })}
      ${kpi({ label: 'Stake · window', value: fmtKobo(t.duel_handle_kobo), hint: 'gross stake volume' })}
      ${kpi({ label: 'Rake · window', value: fmtKobo(t.duel_rake_kobo), hint: 'house cut' })}
    </div>
  `;

  const rates = `
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(186px,1fr));">
      ${_rate('Accept rate', fmtBps(t.accept_rate_bps), t.accept_rate_bps, '#0E7C5A', 'accepted ÷ created')}
      ${_rate('Abandon rate', fmtBps(t.abandon_rate_bps), t.abandon_rate_bps, '#D92D20', 'declined+abandoned ÷ created')}
      ${_rate('Paid share', fmtBps(t.paid_share_bps), t.paid_share_bps, '#1E3A8A', 'paid ÷ all duels')}
      ${_rate('Friend-duel share', fmtBps(t.friend_duel_share_bps), t.friend_duel_share_bps, '#7C53E8', 'friend ÷ completed')}
      ${_rate('Duels / active player', (Number(t.duels_per_active_player_x100 || 0) / 100).toFixed(2), t.duels_per_active_player_x100, '#566179', 'completed ÷ active')}
    </div>
  `;

  const funnel = funnelStages([
    {
      stage: 'Created',
      value: fmtInt(created),
      dropText: 'entry',
      pct: 100,
      color: '#1E3A8A',
    },
    {
      stage: 'Accepted',
      value: fmtInt(accepted),
      dropText: `${_pctText(accepted, created)} accept`,
      pct: _pct(accepted, created),
      color: '#2C5BC4',
    },
    {
      stage: 'Completed',
      value: fmtInt(completed),
      dropText: `${_pctText(completed, accepted)} of accepted`,
      pct: _pct(completed, created),
      color: '#0E7C5A',
    },
  ]);

  const outcomes = `
    <div style="display:flex;flex-direction:column;gap:16px;">
      ${_outcome('Abandoned', fmtInt(abandoned), 'mid-duel quits', 'var(--a-bad)', 'var(--a-bad-bg)', 'var(--a-bad-bd)')}
      ${_outcome('Declined', fmtInt(declined), 'never accepted', 'var(--a-warn)', 'var(--a-warn-bg)', 'var(--a-warn-bd)')}
    </div>
  `;

  const chart = `<div id="adm2-duels-chart"></div>`;

  const grid = `
    <div class="adm2-grid">
      ${kpi({ label: 'Created', value: fmtInt(t.duels_created), hint: 'this period' })}
      ${kpi({ label: 'Accepted', value: fmtInt(t.duels_accepted), hint: 'this period' })}
      ${kpi({ label: 'Completed', value: fmtInt(t.duels_completed), hint: 'this period' })}
      ${kpi({ label: 'Abandoned', value: fmtInt(t.duels_abandoned), hint: 'mid-duel quits' })}
      ${kpi({ label: 'Declined', value: fmtInt(t.duels_declined), hint: 'never accepted' })}
      ${kpi({ label: 'Paid duels', value: fmtInt(t.duels_paid_count), hint: 'staked' })}
      ${kpi({ label: 'Free duels', value: fmtInt(t.duels_free_count), hint: 'no stake' })}
      ${kpi({ label: 'Avg stake', value: fmtKobo(t.duel_avg_stake_kobo), hint: 'per paid duel' })}
      ${kpi({ label: 'Avg matchmaking wait', value: `${fmtInt(t.duel_avg_matchmaking_wait_seconds)} s`, hint: 'queue time' })}
      ${kpi({ label: 'Active players', value: fmtInt(t.active_players), hint: 'played' })}
    </div>
  `;

  return `
    ${snapshot}
    ${rates}
    <div class="adm2-two" style="margin-top:18px;">
      <div class="adm2-panel">
        <div class="adm2-panel-title">Duel lifecycle funnel</div>
        <div class="adm2-panel-sub">created → accepted → completed</div>
        <div style="margin-top:18px;">${funnel}</div>
      </div>
      ${outcomes}
    </div>

    <div class="adm2-panel" style="margin-top:16px;">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px;flex-wrap:wrap;">
        <div>
          <div class="adm2-panel-title">Duels created vs completed</div>
          <div class="adm2-panel-sub">daily/weekly buckets · illustrative until dense data lands</div>
        </div>
      </div>
      <div style="margin-top:16px;">${chart}</div>
    </div>

    <div class="adm2-section-label">Daily activity</div>
    <div class="adm2-two">
      <div class="adm2-panel">
        <div class="adm2-panel-title">Duels created</div>
        <div class="adm2-panel-sub">hover a bar for accepted / completed on that day</div>
        <div id="adm2-duels-created-chart" style="margin-top:14px;"></div>
      </div>
      <div class="adm2-panel">
        <div class="adm2-panel-title">Stake volume</div>
        <div class="adm2-panel-sub">hover a bar for handle + rake on that day</div>
        <div id="adm2-duels-stake-chart" style="margin-top:14px;"></div>
      </div>
    </div>

    <div class="adm2-panel" style="padding:0;overflow:hidden;margin-top:18px;">
      <div style="padding:18px 20px;border-bottom:1px solid var(--a-border);">
        <div class="adm2-panel-title">Recent duels</div>
        <div class="adm2-panel-sub">windowed by the date-range picker · click a row for the client-integrity drill-down</div>
      </div>
      <div id="adm2-recent-duels-list" style="padding:8px 4px;"></div>
    </div>

    <div class="adm2-panel" style="padding:0;overflow:hidden;margin-top:18px;">
      <div style="padding:18px 20px;border-bottom:1px solid var(--a-border);">
        <div class="adm2-panel-title">Top 1-v-1 players</div>
        <div class="adm2-panel-sub">all-time · games / wins / stake volume · click a header to sort</div>
      </div>
      <div id="adm2-duel-players-list" style="padding:8px 4px;"></div>
    </div>

    <div class="adm2-section-label">All duel metrics · windowed</div>
    ${grid}
  `;
}

function _mountCharts(host, d) {
  const chartHost = host.querySelector('#adm2-duels-chart');
  const series = d.timeseries || [];
  mountLineChart(chartHost, {
    series: [
      { name: 'Created', color: '#1E3A8A', values: series.map((r) => Number(r.duels_created || 0)) },
      { name: 'Completed', color: '#0E7C5A', values: series.map((r) => Number(r.duels_completed || 0)) },
    ],
    xLabels: series.map((r) => r.date),
    height: 230,
    emptyMessage: 'No duels in this window.',
  });
}

function _rate(label, value, pctBps, color, hint) {
  const pct = Math.min(100, Math.max(0, Number(pctBps || 0) / 100));
  return `
    <div style="background:var(--a-panel);border:1px solid var(--a-border);border-radius:14px;padding:17px;box-shadow:0 1px 2px rgba(16,24,40,.05);">
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.05em;text-transform:uppercase;color:var(--a-muted);">${escape(label)}</div>
      <div style="font:600 22px 'IBM Plex Mono',monospace;color:var(--a-ink);margin-top:11px;">${escape(value)}</div>
      <div class="adm2-bar-track" style="height:7px;margin-top:12px;">
        <div class="adm2-bar-fill" style="width:${pct}%;background:${color};"></div>
      </div>
      <div style="font:500 9px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:9px;">${escape(hint)}</div>
    </div>
  `;
}
function _outcome(label, value, sub, color, bg, bd) {
  return `
    <div style="flex:1;background:${bg};border:1px solid ${bd};border-radius:16px;padding:20px;">
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.05em;text-transform:uppercase;color:${color};">${escape(label)}</div>
      <div style="font:600 30px 'IBM Plex Mono',monospace;color:${color};margin-top:10px;opacity:.92;">${escape(value)}</div>
      <div style="font:500 11px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:6px;">${escape(sub)}</div>
    </div>
  `;
}
function _pct(n, d) {
  const nn = Number(n || 0);
  const dd = Number(d || 0);
  if (dd <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((100 * nn) / dd)));
}
function _pctText(n, d) {
  return `${_pct(n, d)}%`;
}

function _formatDayLabel(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
}

function _mountDailyCharts(host, d) {
  const ts = d.timeseries || [];
  const createdHost = host.querySelector('#adm2-duels-created-chart');
  const stakeHost = host.querySelector('#adm2-duels-stake-chart');
  if (createdHost) {
    mountInteractiveBarChart(createdHost, {
      data: ts.map((row) => ({
        x: row.date,
        y: Number(row.duels_created || 0),
        meta: row,
      })),
      color: '#1E3A8A',
      yLabel: 'created',
      xFmt: _formatDayLabel,
      tooltipExtra: (row) => `
        <div><span class="k">Accepted</span><span class="v">${escape(fmtInt(row.meta?.duels_accepted))}</span></div>
        <div><span class="k">Completed</span><span class="v">${escape(fmtInt(row.meta?.duels_completed))}</span></div>
      `,
      emptyMessage: 'No duels in this window.',
    });
  }
  if (stakeHost) {
    mountInteractiveBarChart(stakeHost, {
      data: ts.map((row) => ({
        x: row.date,
        y: Number(row.duel_handle_kobo || 0),
        meta: row,
      })),
      color: '#0E7C5A',
      yLabel: 'stake',
      xFmt: _formatDayLabel,
      valueFmt: (v) => fmtKobo(v),
      tooltipExtra: (row) => `
        <div><span class="k">Rake</span><span class="v">${escape(fmtKobo(row.meta?.duel_rake_kobo))}</span></div>
        <div><span class="k">Prizes paid</span><span class="v">${escape(fmtKobo(row.meta?.duel_prizes_paid_kobo))}</span></div>
      `,
      emptyMessage: 'No stake volume in this window.',
    });
  }
}

function _mountTopPlayers(host, ctx, payload) {
  const target = host.querySelector('#adm2-duel-players-list');
  if (!target) return;
  const items = (payload && payload.items) || [];
  if (!items.length) {
    target.innerHTML =
      '<div style="padding:32px 16px;text-align:center;color:var(--a-faint);">No finished duels yet.</div>';
    return;
  }
  mountSortableTable(target, {
    columns: [
      {
        key: 'user',
        label: 'Player',
        text: true,
        get: (r) => (r.email || '').toLowerCase(),
        cell: (r) => `
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
            <span style="font:600 13px 'IBM Plex Sans',sans-serif;color:var(--a-ink);">${escape(r.username || (r.email || '').split('@')[0] || 'user')}</span>
            ${r.is_admin ? '<span class="adm2-chip" style="color:var(--a-brand);background:var(--a-brand-soft);border-color:transparent;">ADMIN</span>' : ''}
          </div>
          <div style="font:400 11px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:3px;">${escape(r.email || '')}</div>
        `,
      },
      {
        key: 'games',
        label: 'Games',
        right: true,
        get: (r) => Number(r.games || 0),
        cell: (r) => escape(fmtInt(r.games)),
        className: 'mono',
      },
      {
        key: 'wins',
        label: 'Wins',
        right: true,
        get: (r) => Number(r.wins || 0),
        cell: (r) => `<span style="color:var(--a-ok);">${escape(fmtInt(r.wins))}</span>`,
        className: 'mono',
      },
      {
        key: 'losses',
        label: 'Losses',
        right: true,
        get: (r) => Number(r.losses || 0),
        cell: (r) => escape(fmtInt(r.losses)),
        className: 'mono',
      },
      {
        key: 'win_rate_bps',
        label: 'Win rate',
        right: true,
        get: (r) => Number(r.win_rate_bps || 0),
        cell: (r) => escape(fmtBps(r.win_rate_bps)),
        className: 'mono',
      },
      {
        key: 'stake_kobo',
        label: 'Stake volume',
        right: true,
        get: (r) => Number(r.stake_kobo || 0),
        cell: (r) => escape(fmtKobo(r.stake_kobo)),
        className: 'mono',
      },
    ],
    rows: items,
    defaultSort: { key: 'games', dir: 'desc' },
    minWidth: 820,
    onRowClick: (row) => ctx.navigate(`/admin/v2/players/${row.user_id}`),
    pageSizeKey: 'duels.players',
    emptyMessage: 'No players to show.',
  });
}


// ---------------------------------------------------------------------------
// Recent duels · date-filtered list with per-duel integrity signals
//
// Row-click drills into `/admin/v2/duels/:id` which renders the per-run
// signal breakdown. The list itself carries just the roll-up chip strip
// so an admin scanning the table can spot bot-tell games without
// opening every one.
// ---------------------------------------------------------------------------

function _mountRecentDuels(host, ctx, payload) {
  const target = host.querySelector('#adm2-recent-duels-list');
  if (!target) return;
  const items = (payload && payload.items) || [];
  if (!items.length) {
    target.innerHTML =
      '<div style="padding:32px 16px;text-align:center;color:var(--a-faint);">No duels in this range.</div>';
    return;
  }
  mountSortableTable(target, {
    columns: [
      {
        key: 'created_at',
        label: 'When',
        get: (r) => new Date(r.created_at || 0).getTime(),
        cell: (r) => `<span style="color:var(--a-muted);">${escape(fmtRelTime(r.created_at))}</span>`,
        className: 'mono',
      },
      {
        key: 'type',
        label: 'Type',
        text: true,
        get: (r) => (r.type || ''),
        cell: (r) => `<span class="adm2-chip" style="background:var(--a-panel-2);color:var(--a-muted);">${escape(r.type || '')}</span>`,
      },
      {
        key: 'status',
        label: 'Status',
        text: true,
        get: (r) => (r.status || ''),
        cell: (r) => `<span class="adm2-chip" style="background:var(--a-panel-2);color:var(--a-muted);">${escape(r.status || '')}</span>`,
      },
      {
        key: 'creator',
        label: 'Challenger',
        text: true,
        get: (r) => ((r.creator && r.creator.email) || '').toLowerCase(),
        cell: (r) => _playerCellHtml(r.creator),
      },
      {
        key: 'opponent',
        label: 'Opponent',
        text: true,
        get: (r) => ((r.opponent && r.opponent.email) || '').toLowerCase(),
        cell: (r) =>
          r.opponent
            ? _playerCellHtml(r.opponent)
            : '<span style="color:var(--a-faint);">— open —</span>',
      },
      {
        key: 'stake_kobo',
        label: 'Stake',
        right: true,
        get: (r) => Number(r.stake_kobo || 0),
        cell: (r) => {
          const src = r.stake_source && r.stake_source !== 'main' ? ` <span style="color:var(--a-muted);font-size:10px;">(${escape(r.stake_source)})</span>` : '';
          return `<span style="color:var(--a-ink);">${escape(fmtKobo(r.stake_kobo))}</span>${src}`;
        },
        className: 'mono',
      },
      {
        key: 'fiftyfifty',
        label: '50/50',
        right: true,
        get: (r) => Number(r.fiftyfifty_used_total || 0),
        cell: (r) =>
          r.fiftyfifty_used_total
            ? `<span style="color:var(--a-warn);">${escape(fmtInt(r.fiftyfifty_used_total))}</span>`
            : `<span style="color:var(--a-faint);">0</span>`,
        className: 'mono',
      },
      {
        key: 'integrity',
        label: 'Integrity',
        get: (r) => integritySortKey(r.integrity_aggregates),
        cell: (r) => integrityChips(r.integrity_aggregates, { compact: true }),
      },
    ],
    rows: items,
    defaultSort: { key: 'created_at', dir: 'desc' },
    minWidth: 940,
    onRowClick: (row) => ctx.navigate(`/admin/v2/duels/${row.id}`),
    pageSizeKey: 'duels.recent',
    emptyMessage: 'No duels in this range.',
  });
  // Silence unused-import lint if navigate ever falls out — it's the
  // fallback for pages that reach this file without ctx.navigate.
  void navigate;
}

function _playerCellHtml(p) {
  if (!p) return '';
  const name = p.username || (p.email || '').split('@')[0] || (p.id || '').slice(0, 8);
  return `
    <div style="display:flex;flex-direction:column;">
      <span style="font:600 12.5px 'IBM Plex Sans',sans-serif;color:var(--a-ink);">${escape(name)}</span>
      <span style="font:400 10.5px 'IBM Plex Mono',monospace;color:var(--a-faint);">${escape(p.email || '')}</span>
    </div>
  `;
}
